/**
 * Country name/code → flag image URL resolver
 * Uses flagcdn.com for countries, Flagpedia for international grouping.
 */

const INTERNATIONAL_FLAG_URL = 'https://flagpedia.net/data/org/w40/eu.png';
const INTERNATIONAL_FLAG_KEYS = new Set(['int', 'international', 'world', 'global', 'europe', '국제', '세계']);

const normalizedFlagKey = (value) => String(value || '').trim().toLowerCase();
const isInternationalFlagKey = (value) => INTERNATIONAL_FLAG_KEYS.has(normalizedFlagKey(value));

const COUNTRY_CODE_MAP = {
  // A
  'afghanistan': 'af', 'albania': 'al', 'algeria': 'dz', 'andorra': 'ad',
  'angola': 'ao', 'argentina': 'ar', 'armenia': 'am', 'australia': 'au',
  'austria': 'at', 'azerbaijan': 'az',
  // B
  'bahrain': 'bh', 'bangladesh': 'bd', 'belarus': 'by', 'belgium': 'be',
  'benin': 'bj', 'bolivia': 'bo', 'bosnia-herz': 'ba', 'bosnia and herzegovina': 'ba',
  'bosnia': 'ba', 'botswana': 'bw', 'brazil': 'br', 'brunei': 'bn',
  'bulgaria': 'bg', 'burkina faso': 'bf', 'burundi': 'bi',
  // C
  'cambodia': 'kh', 'cameroon': 'cm', 'canada': 'ca', 'cape verde': 'cv',
  'central african republic': 'cf', 'chad': 'td', 'chile': 'cl', 'china': 'cn',
  'colombia': 'co', 'comoros': 'km', 'congo': 'cg', 'costa rica': 'cr',
  'croatia': 'hr', 'cuba': 'cu', 'cyprus': 'cy', 'czech republic': 'cz', 'czechia': 'cz',
  // D-E
  'denmark': 'dk', 'djibouti': 'dj', 'dominican republic': 'do',
  'ecuador': 'ec', 'egypt': 'eg', 'el salvador': 'sv', 'equatorial guinea': 'gq',
  'eritrea': 'er', 'estonia': 'ee', 'eswatini': 'sz', 'ethiopia': 'et',
  // UK subdivisions (flagcdn supports these)
  'england': 'gb-eng', 'scotland': 'gb-sct', 'wales': 'gb-wls',
  'northern ireland': 'gb-nir',
  // F-G
  'finland': 'fi', 'france': 'fr', 'gabon': 'ga', 'gambia': 'gm',
  'georgia': 'ge', 'germany': 'de', 'ghana': 'gh', 'greece': 'gr',
  'guatemala': 'gt', 'guinea': 'gn', 'guinea-bissau': 'gw',
  // H-I
  'haiti': 'ht', 'honduras': 'hn', 'hong kong': 'hk', 'hungary': 'hu',
  'iceland': 'is', 'india': 'in', 'indonesia': 'id', 'iran': 'ir',
  'iraq': 'iq', 'ireland': 'ie', 'israel': 'il', 'italy': 'it',
  'ivory coast': 'ci', "cote d'ivoire": 'ci',
  // J-K
  'jamaica': 'jm', 'japan': 'jp', 'jordan': 'jo',
  'kazakhstan': 'kz', 'kenya': 'ke', 'korea republic': 'kr', 'south korea': 'kr',
  'korea': 'kr', 'north korea': 'kp', 'kuwait': 'kw', 'kyrgyzstan': 'kg',
  // L
  'laos': 'la', 'latvia': 'lv', 'lebanon': 'lb', 'lesotho': 'ls',
  'liberia': 'lr', 'libya': 'ly', 'liechtenstein': 'li', 'lithuania': 'lt',
  'luxembourg': 'lu',
  // M
  'madagascar': 'mg', 'malawi': 'mw', 'malaysia': 'my', 'maldives': 'mv',
  'mali': 'ml', 'malta': 'mt', 'mauritania': 'mr', 'mauritius': 'mu',
  'mexico': 'mx', 'moldova': 'md', 'monaco': 'mc', 'mongolia': 'mn',
  'montenegro': 'me', 'morocco': 'ma', 'mozambique': 'mz', 'myanmar': 'mm',
  // N
  'namibia': 'na', 'nepal': 'np', 'netherlands': 'nl', 'new zealand': 'nz',
  'nicaragua': 'ni', 'niger': 'ne', 'nigeria': 'ng', 'north macedonia': 'mk',
  'norway': 'no',
  // O-P
  'oman': 'om', 'pakistan': 'pk', 'palestine': 'ps', 'panama': 'pa',
  'papua new guinea': 'pg', 'paraguay': 'py', 'peru': 'pe', 'philippines': 'ph',
  'poland': 'pl', 'portugal': 'pt',
  // Q-R
  'qatar': 'qa', 'romania': 'ro', 'russia': 'ru', 'rwanda': 'rw',
  // S
  'saudi arabia': 'sa', 'senegal': 'sn', 'serbia': 'rs',
  'sierra leone': 'sl', 'singapore': 'sg', 'slovakia': 'sk', 'slovenia': 'si',
  'somalia': 'so', 'south africa': 'za', 'south sudan': 'ss',
  'spain': 'es', 'sri lanka': 'lk', 'sudan': 'sd', 'suriname': 'sr',
  'sweden': 'se', 'switzerland': 'ch', 'syria': 'sy',
  // T
  'taiwan': 'tw', 'tajikistan': 'tj', 'tanzania': 'tz', 'thailand': 'th',
  'togo': 'tg', 'trinidad and tobago': 'tt', 'tunisia': 'tn',
  'turkey': 'tr', 'turkiye': 'tr', 'turkmenistan': 'tm',
  // U
  'uganda': 'ug', 'ukraine': 'ua', 'united arab emirates': 'ae', 'uae': 'ae',
  'united kingdom': 'gb', 'united states': 'us', 'usa': 'us',
  'uruguay': 'uy', 'uzbekistan': 'uz',
  // V-Z
  'venezuela': 've', 'vietnam': 'vn', 'yemen': 'ye',
  'zambia': 'zm', 'zimbabwe': 'zw',
};

/**
 * Resolve country flag URL from multiple possible sources
 * @param {object} league - league object with country/countryInfo fields
 * @returns {string|null} flagcdn URL or null
 */
export function getCountryFlagUrl(league) {
  if (!league) return null;

  const ci = league.countryInfo || {};

  // Priority 1: direct alpha-2 code fields
  const codeFields = [
    league.country_code,
    league.countryKey,
    league.country_key,
    ci.code,
    ci.alpha2,
    ci.key,
  ];
  for (const code of codeFields) {
    if (isInternationalFlagKey(code)) return INTERNATIONAL_FLAG_URL;
    if (code && code.length >= 2) {
      return `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
    }
  }

  // Priority 2: country name → code mapping
  const nameFields = [
    league.country,
    ci.name,
    ci.name_ko,
  ];
  for (const name of nameFields) {
    if (!name) continue;
    const key = name.toLowerCase().trim();
    if (isInternationalFlagKey(key)) return INTERNATIONAL_FLAG_URL;
    const code = COUNTRY_CODE_MAP[key];
    if (code) return `https://flagcdn.com/w40/${code}.png`;
  }

  return null;
}

export default getCountryFlagUrl;
